import { RETIRED_USAGE_TAB_KEYS } from "./retired-usage-tabs.js";
/**
 * Functionality tabs are entity-page tabs the product injects, not tabs the framework
 * authors: no `frameworkEntityViewTabs` row, no template fields, no rich-text body.
 * The keys are the per-entity-type on/off switches stored on
 * `frameworkEntityTypeSettings.functionalityTabs` and compiled into
 * `CompiledEntityView.functionalityTabs`.
 */
export const FRAMEWORK_FUNCTIONALITY_TAB_KEYS = ["attachments", "usage"];
/**
 * The single source of truth for injected entity tabs. Every injection site — the web
 * app's tab strip, the Claudia/Quality Guard authoring schema, the page body dispatch —
 * reads this list instead of hardcoding tab keys.
 */
export const SYNTHETIC_ENTITY_TABS = [
    {
        functionalityKey: "usage",
        tabKey: "entity.usage",
        label: "Usage",
        syntheticOrder: 100,
        // Usage is computed from mentions and relations as they are *now*; showing it beside
        // a historical version would pair today's answer with another point in time.
        versionPolicy: "current-only",
        defaultEnabled: true,
    },
    {
        functionalityKey: "attachments",
        tabKey: "entity.attachments",
        label: "Attachments",
        // Attachments is deliberately last; tabs added later take a lower order.
        syntheticOrder: 200,
        versionPolicy: "always",
        defaultEnabled: true,
    },
];
export const SYNTHETIC_ENTITY_TAB_KEYS = new Set(SYNTHETIC_ENTITY_TABS.map((definition) => definition.tabKey));
export function isSyntheticEntityTabKey(tabKey) {
    return typeof tabKey === "string" && SYNTHETIC_ENTITY_TAB_KEYS.has(tabKey);
}
export function getSyntheticEntityTab(tabKey) {
    if (typeof tabKey !== "string")
        return null;
    return SYNTHETIC_ENTITY_TABS.find((definition) => definition.tabKey === tabKey) ?? null;
}
export function findSyntheticEntityTab(functionalityKey) {
    return (SYNTHETIC_ENTITY_TABS.find((definition) => definition.functionalityKey === functionalityKey) ??
        null);
}
/** Registry lookup for call sites that own a tab key of their own — throws if it drifts. */
export function requireSyntheticEntityTab(functionalityKey) {
    const definition = findSyntheticEntityTab(functionalityKey);
    if (definition === null) {
        throw new Error(`No synthetic entity tab is registered for '${functionalityKey}'.`);
    }
    return definition;
}
function resolveDefaultFunctionalityTabEnabled(functionalityKey) {
    // Every functionality key has a registered tab today. The fallback keeps a key whose
    // tab is added later (or removed ahead of its flag) resolving to enabled rather than
    // throwing inside the framework compile path.
    return findSyntheticEntityTab(functionalityKey)?.defaultEnabled ?? true;
}
/**
 * Absent means enabled. The stored flags are an opt-out for entity types that should not
 * carry a functionality tab — treating them as an opt-in list would require backfilling
 * every existing settings row before the tab reappeared. A missing entity view is the
 * one negative case: with no compiled view there is no entity page to hang a tab on.
 */
export function resolveFunctionalityTabEnabled(entityView, functionalityKey) {
    if (entityView === null || entityView === undefined)
        return false;
    return (entityView.functionalityTabs?.[functionalityKey] ??
        resolveDefaultFunctionalityTabEnabled(functionalityKey));
}
/** Expands stored (sparse) flags into the total record compiled into an entity view. */
export function resolveCompiledFunctionalityTabs(storedFlags) {
    const resolved = {};
    for (const key of FRAMEWORK_FUNCTIONALITY_TAB_KEYS) {
        resolved[key] = storedFlags?.[key] ?? resolveDefaultFunctionalityTabEnabled(key);
    }
    return resolved;
}
/** The synthetic tabs an entity type gets, in injection order. */
export function resolveSyntheticEntityTabs(entityView) {
    if (entityView === null || entityView === undefined)
        return [];
    return SYNTHETIC_ENTITY_TABS.filter((definition) => resolveFunctionalityTabEnabled(entityView, definition.functionalityKey)).sort((left, right) => left.syntheticOrder - right.syntheticOrder);
}
/**
 * The authored tabs an entity page renders, in list order: enabled, and not one of the
 * hand-written usage tabs the synthetic Usage tab replaced.
 *
 * Every surface that lists an element's tabs — the tab strip, links to a tab, the export
 * dialog — reads this instead of filtering `entityView.tabs` itself, so a tab hidden on
 * screen is hidden everywhere. The retired-key filter is a deprecation shim: a retained
 * environment keeps serving those tabs from its compiled structure view until the removal
 * mutation and `frameworks:refreshStructureViews` have run there, and without it such an
 * environment would show two "Usage" tabs. Delete the filter once every environment has run
 * the removal and the refresh, and the tabs have been verified gone.
 */
export function resolveVisibleAuthoredEntityTabs(entityView) {
    if (entityView === null || entityView === undefined)
        return [];
    return [...(entityView.tabs ?? [])]
        .filter((tab) => tab.disabledAt == null && !RETIRED_USAGE_TAB_KEYS.has(tab.tabKey))
        .sort((left, right) => left.listOrder - right.listOrder);
}
/**
 * Appends the enabled synthetic tabs after the authored tabs, continuing their
 * `listOrder` sequence so downstream sorting keeps them last.
 */
export function appendSyntheticEntityTabs(baseTabs, entityView) {
    const syntheticTabs = resolveSyntheticEntityTabs(entityView);
    if (syntheticTabs.length === 0)
        return [...baseTabs];
    const maxListOrder = baseTabs.reduce((max, tab) => Math.max(max, tab.listOrder), 0);
    return [
        ...baseTabs,
        ...syntheticTabs.map((definition, index) => ({
            tabKey: definition.tabKey,
            listOrder: maxListOrder + index + 1,
            disabledAt: null,
            ownerFieldId: null,
            configJson: { label: definition.label },
        })),
    ];
}
